import React, { useState, useEffect, useContext } from 'react';
import { Table, Button, InputNumber, Typography, Select, Input, Space, message, DatePicker } from 'antd';
import axios from 'axios';
import dayjs from 'dayjs';
import { useMovimientos } from '../../context/movimientosContext';
import { LoginContext } from '../../context/loginContext';

const { Title, Text } = Typography;
const { Option } = Select;

// Pequeño wrapper para poner un label arriba de cada campo
const Labeled = ({ label, width, children }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 4, width }}>
    <Text type="secondary" style={{ fontSize: 12 }}>{label}</Text>
    {children}
  </div>
);

export const ArticuloSelector = () => {
  const [articulos, setArticulos] = useState([]);
  const [filteredArticulos, setFilteredArticulos] = useState([]);
  const [selectedArticulo, setSelectedArticulo] = useState(null);
  const [letra, setLetra] = useState(null);
  const [num1, setNum1] = useState(null);
  const [num2, setNum2] = useState(null);
  const [cantidad, setCantidad] = useState(0);
  const [lote, setLote] = useState('');
  const [fechaVencimiento, setFechaVencimiento] = useState(null);
  const [searchFilters, setSearchFilters] = useState({});
  const { user } = useContext(LoginContext);
  const { articulosMovimientos, addArticuloMovimiento, addPosicionToArticulo } = useMovimientos();

  useEffect(() => { fetchArticulos(); }, []);
  useEffect(() => { applyFilters(); }, [searchFilters, articulos]);

  const fetchArticulos = async () => {
    try {
      const { data } = await axios.get(`${process.env.REACT_APP_API_URL}/articulos`, {
        headers: { Authorization: `Bearer ${user.token}` },
      });
      setArticulos(data);
    } catch {
      message.error('Error al obtener artículos');
    }
  };

  const applyFilters = () => {
    const filtered = articulos.filter((art) => {
      return Object.keys(searchFilters).every((key) => {
        const value = art[key];
        const comparisonValue = typeof value === 'object' && value !== null ? value.nombre : value;
        return comparisonValue?.toString().toLowerCase().includes(searchFilters[key]?.toLowerCase());
      });
    });
    setFilteredArticulos(filtered);
  };

  const handleSearch = (key, value) => setSearchFilters(prev => ({ ...prev, [key]: value }));

  const handleArticuloSelection = (articulo) => {
    setSelectedArticulo(articulo);
    setLetra(null); setNum1(null); setNum2(null);
    setCantidad(0); setLote(''); setFechaVencimiento(null);
  };

  const handleAgregarPosicion = () => {
    const posicion = `${letra}-${num1}-${num2}`;
    if (selectedArticulo && letra && num1 && num2 && cantidad > 0) {
      addArticuloMovimiento(selectedArticulo);
      const fv = fechaVencimiento ? dayjs(fechaVencimiento).format('YYYY-MM-DD') : null;
      addPosicionToArticulo(selectedArticulo._id, posicion, cantidad, {
        lote: (lote || '').trim() || null,
        fechaVencimiento: fv
      });
      message.success('Artículo y posición agregados al movimiento');
      setSelectedArticulo(null);
      setLetra(null); setNum1(null); setNum2(null);
      setCantidad(0); setLote(''); setFechaVencimiento(null);
    } else {
      message.warning('Seleccione un artículo, posición y cantidad válida');
    }
  };

  const disabledAdd = !selectedArticulo || !letra || !num1 || !num2 || !cantidad;

  const articuloColumns = [
    {
      title: 'Artículo',
      dataIndex: 'nombre',
      key: 'nombre',
      filterDropdown: () => (
        <Input placeholder="Buscar Artículo" onChange={(e) => handleSearch('nombre', e.target.value)} style={{ marginBottom: 8 }} />
      ),
    },
    {
      title: 'Marca',
      dataIndex: 'idmarca',
      key: 'idmarca',
      render: (idmarca) => (idmarca ? idmarca.nombre : 'N/A'),
      filterDropdown: () => (
        <Input placeholder="Buscar Marca" onChange={(e) => handleSearch('idmarca', e.target.value)} style={{ marginBottom: 8 }} />
      ),
    },
    {
      title: 'Categoría',
      dataIndex: 'idcategoria',
      key: 'idcategoria',
      render: (idcategoria) => (idcategoria ? idcategoria.nombre : 'N/A'),
      filterDropdown: () => (
        <Input placeholder="Buscar Categoría" onChange={(e) => handleSearch('idcategoria', e.target.value)} style={{ marginBottom: 8 }} />
      ),
    },
    { title: 'Unidad de Medida', dataIndex: 'unidadDeMedida', key: 'unidadDeMedida' },
    {
      title: 'Acciones',
      key: 'actions',
      render: (_, record) => (
        <Button type="primary" onClick={() => handleArticuloSelection(record)}>
          Seleccionar
        </Button>
      ),
    },
  ];

  const posicionesColumns = [
    { title: 'Artículo', dataIndex: ['articulo', 'nombre'], key: 'nombre' },
    { title: 'Posición', dataIndex: 'posicion', key: 'posicion' },
    { title: 'Cantidad', dataIndex: 'cantidad', key: 'cantidad' },
    { title: 'Lote', dataIndex: 'lote', key: 'lote', render: v => v || '—' },
    {
      title: 'Vencimiento',
      dataIndex: 'fechaVencimiento',
      key: 'fechaVencimiento',
      render: v => v ? dayjs(v).format('YYYY-MM-DD') : '—'
    },
  ];

  const posicionesData = articulosMovimientos.flatMap(a =>
    a.posiciones.map(p => ({
      key: `${a.articulo._id}-${p.posicion}-${p.lote || 'nl'}-${p.fechaVencimiento || 'nf'}`,
      articulo: a.articulo,
      posicion: p.posicion,
      cantidad: p.cantidad,
      lote: p.lote || null,
      fechaVencimiento: p.fechaVencimiento || null,
    }))
  );

  return (
    <div>
      <Title level={4}>Seleccione los artículos y posiciones</Title>

      <Table
        columns={articuloColumns}
        dataSource={filteredArticulos}
        pagination={{ pageSize: 5 }}
        rowKey="_id"
        style={{ marginBottom: 20 }}
      />

      {selectedArticulo && (
        <div style={{ marginTop: 20 }}>
          <Title level={5}>Artículo Seleccionado: {selectedArticulo.nombre}</Title>
          <Space wrap align="end">
            <Labeled label="Letra" width={100}>
              <Select placeholder="Seleccionar" value={letra} onChange={setLetra}>
                {Array.from({ length: 26 }, (_, i) => String.fromCharCode(65 + i)).map(letter => (
                  <Option key={letter} value={letter}>{letter}</Option>
                ))}
              </Select>
            </Labeled>

            <Labeled label="Pos 1" width={120}>
              <InputNumber min={1} max={50} placeholder="1–50" value={num1} onChange={setNum1} style={{ width: '100%' }} />
            </Labeled>

            <Labeled label="Pos 2" width={120}>
              <InputNumber min={1} max={50} placeholder="1–50" value={num2} onChange={setNum2} style={{ width: '100%' }} />
            </Labeled>

            <Labeled label="Cantidad" width={120}>
              <InputNumber 
                min={0} 
                step={0.01}
                precision={2}
                value={cantidad} 
                onChange={setCantidad} 
                style={{ width: '100%' }} 
              />
            </Labeled>

            <Labeled label="N° de lote (opcional)" width={180}>
              <Input placeholder="Ej: L2025-001" value={lote} onChange={e => setLote(e.target.value)} />
            </Labeled>

            <Labeled label="Vencimiento (opcional)" width={180}>
              <DatePicker
                placeholder="YYYY-MM-DD"
                value={fechaVencimiento ? dayjs(fechaVencimiento) : null}
                onChange={setFechaVencimiento}
                format="YYYY-MM-DD"
                allowClear
                style={{ width: '100%' }}
              />
            </Labeled>

            <Button type="primary" onClick={handleAgregarPosicion} disabled={disabledAdd}>
              Agregar Posición
            </Button>
          </Space>
        </div>
      )}

      <div style={{ marginTop: 20 }}>
        <Title level={5}>Posiciones Agregadas</Title>
        <Table columns={posicionesColumns} dataSource={posicionesData} pagination={{ pageSize: 5 }} rowKey="key" />
      </div>
    </div>
  );
};

export default ArticuloSelector;